import {EnhancementReportStat} from '../../../../models/enhancement/enhancement-report-stats'
import {EnhancementViewLevel} from '../../ReportViewLevelSelector'

const MAX_EMPLOYEE_RESULTS = 20

export function processChartData(data: EnhancementReportStat[], selectedRequestTypes?: string[] | null, viewLevel: EnhancementViewLevel = EnhancementViewLevel.DEPARTMENT) {
  if (!data?.length) {
    return {
      departments: [],
      approvedOnCallHours: [],
      callOutToOnCallRatios: []
    }
  }

  // Filter data by request types if specified
  const filteredData = selectedRequestTypes && selectedRequestTypes.length > 0
    ? data.filter(item => selectedRequestTypes.includes(item.requestType))
    : data

  // Pre-calculate employee stats for better performance
  const employeeStats = viewLevel === EnhancementViewLevel.EMPLOYEE
    ? filteredData.reduce((acc, stat) => {
      if (!acc[stat.employee]) {
        acc[stat.employee] = {
          count: 0,
          departments: new Set<string>()
        }
      }
      acc[stat.employee].count += 1
      acc[stat.employee].departments.add(stat.department)
      return acc
    }, {} as Record<string, { count: number; departments: Set<string> }>)
    : {}

  const groupingKey = (stat: EnhancementReportStat): string => {
    switch (viewLevel) {
      case EnhancementViewLevel.TEAM:
        return stat.team || 'No Team'
      case EnhancementViewLevel.EMPLOYEE: {
        const stats = employeeStats[stat.employee]
        if (stats.count > 1 && stats.departments.size > 1) {
          return `${stat.employee} (${stat.department})`
        }
        return stat.employee
      }
      case EnhancementViewLevel.DEPARTMENT:
      default:
        return stat.department
    }
  }

  const groupMap = filteredData.reduce((acc, stat) => {
    const key = groupingKey(stat)

    if (!acc.has(key)) {
      acc.set(key, {approvedOnCallHours: 0, approvedCallOutHours: 0})
    }

    const groupData = acc.get(key)!

    if (stat.finalPaidHours) {
      if (stat.requestType === 'On Call') {
        groupData.approvedOnCallHours += stat.finalPaidHours
      } else if (stat.requestType === 'Call Out') {
        groupData.approvedCallOutHours += stat.finalPaidHours
      }
    }

    return acc
  }, new Map<string, { approvedOnCallHours: number, approvedCallOutHours: number }>())

  const filteredGroups = Array.from(groupMap.entries())
    .filter(([_, value]) => value.approvedOnCallHours > 0 || value.approvedCallOutHours > 0)
    .sort((a, b) => b[1].approvedOnCallHours - a[1].approvedOnCallHours)

  const finalGroups = viewLevel === EnhancementViewLevel.EMPLOYEE
    ? filteredGroups.slice(0, MAX_EMPLOYEE_RESULTS)
    : filteredGroups

  return finalGroups.reduce((result, [groupName, value]) => {
    result.departments.push(groupName)
    result.approvedOnCallHours.push(parseFloat(value.approvedOnCallHours.toFixed(2)))

    const ratio = value.approvedOnCallHours > 0
      ? parseFloat((value.approvedCallOutHours / value.approvedOnCallHours).toFixed(2))
      : 0
    result.callOutToOnCallRatios.push(ratio)

    return result
  }, {
    departments: [] as string[],
    approvedOnCallHours: [] as number[],
    callOutToOnCallRatios: [] as number[]
  })
}